import RolePermissions from '../RolePermissions';
import {routeOrViewIsPinned} from './navigationPinUtils';

/**
 * Navigation Logic
 *
 * Core business logic for determining route visibility and suggestions.
 * Combines role permissions, customer settings, and user preferences.
 */

/**
 * Get all visible routes for a user
 * Combines role permissions + customer settings + user pins
 *
 * @param {Object} user - Current user object
 * @param {Object} routePreferences - Customer-level route settings from dataStatus
 * @param {Object} userPreferences - User-level preferences (pins, recents)
 * @param {Array} allRoutes - Full route registry
 * @returns {Array} Filtered list of visible routes
 */
export const getVisibleRoutes = (user, routePreferences = {}, userPreferences = {}, allRoutes = []) => {
  if (!user || !allRoutes.length) return [];

  return allRoutes.filter(route => {
    // 1. Check role permissions (existing RolePermissions.js)
    const hasRoleAccess = RolePermissions[user.role]?.[route.id]?.hasAccess;
    if (!hasRoleAccess) return false;

    if (route.id === 'workforce') {
      const grant = user.accessPermissions?.read?.workforce;
      const support = /^appadmin\+[^@\s]+@qflow\.ai$/i.test(user.email || '');
      const childEntity = user.homeCustomerId && user.homeCustomerId !== user.customerId;
      // Support aliases can navigate to the authorized selected entity. The
      // Workforce API still verifies its active membership and access on load.
      if (user.accountType === 'rollup' || (!support && (childEntity || !['full', 'summary'].includes(grant)))) return false;
    }

    // 2. Special case: Prompt Manager - only for @qflow.ai users
    if (route.id === 'manage-prompts') {
      const isQFlowUser = user.email?.indexOf('@qflow.ai') > -1 ||
                          user.email?.indexOf('blossom') > -1 ||
                          user.email?.indexOf('arosten') > -1;
      if (!isQFlowUser) return false;
    }

    // 3. Check if admin disabled this route for the customer
    const customerSetting = routePreferences[route.id];
    if (customerSetting?.enabled === false) {
      // Exception: Always show if user has it pinned
      if (routeOrViewIsPinned(route.id, userPreferences?.pinnedRoutes)) {
        return true;
      }
      return false;
    }

    // 4. Permanent routes (Recipe Designer/Results) are always visible
    if (route.isPermanent) {
      return true;
    }

    // 5. Check if route requires setup (like GtmStats pattern)
    // This would integrate with dataStatus checks
    // For now, assume all routes with requiresSetup need data setup complete
    // The actual check happens at the component level

    return true;
  });
};

/**
 * Get contextual route suggestions
 * Based on current route, active recipe, and user context
 *
 * @param {string} currentRouteId - Current route ID
 * @param {Object} activeRecipe - Recipe being edited (if any)
 * @param {Object} knowledgebase - Knowledgebase data
 * @param {Array} allRoutes - Full route registry
 * @returns {Array} Suggested routes with reasons and confidence scores
 */
export const getSuggestedRoutes = (currentRouteId, activeRecipe, knowledgebase, allRoutes = []) => {
  const suggestions = new Map(); // Use Map to track scores

  // Helper to find route by tool ID in enriched routes
  const findRouteByToolId = (toolId) => {
    return allRoutes.find(route =>
      route.toolId === toolId || route.relatedTools?.includes(toolId)
    );
  };

  // Score based on recipe tools
  if (activeRecipe?.steps) {
    activeRecipe.steps.forEach(step => {
      const route = findRouteByToolId(step.toolId);
      if (route) {
        const existing = suggestions.get(route.id) || { route, score: 0, reasons: [] };
        existing.score += 3;
        existing.reasons.push(`Recipe uses ${step.toolId} tool`);
        suggestions.set(route.id, existing);
      }
    });
  }

  // Score based on current route's related routes
  if (currentRouteId) {
    const current = allRoutes.find(r => r.id === currentRouteId);
    if (current?.relatedTools) {
      current.relatedTools.forEach(toolId => {
        const route = findRouteByToolId(toolId);
        if (route && route.id !== currentRouteId) {
          const existing = suggestions.get(route.id) || { route, score: 0, reasons: [] };
          existing.score += 2;
          existing.reasons.push('Related analysis');
          suggestions.set(route.id, existing);
        }
      });
    }
  }

  // Convert to array, sort by score, and format
  return Array.from(suggestions.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map(suggestion => ({
      route: suggestion.route,
      reason: suggestion.reasons[0] || suggestion.route.description,
      confidence: Math.min(suggestion.score / 10, 1), // Normalize to 0-1
    }));
};

/**
 * Organize routes by category for display
 *
 * @param {Array} routes - Routes to organize
 * @param {Array} categories - Category names
 * @returns {Array} Routes grouped by category
 */
export const getRoutesByCategory = (routes, categories) => {
  return categories
    .map(category => ({
      name: category,
      routes: routes.filter(r => r.category === category),
    }))
    .filter(cat => cat.routes.length > 0);
};

/**
 * Check if a route should be suggested based on context
 *
 * @param {Object} route - Route to check
 * @param {Object} context - Current navigation context
 * @returns {boolean} Whether route should be suggested
 */
export const shouldSuggestRoute = (route, context) => {
  if (!route.suggestWhen || route.suggestWhen.length === 0) {
    return false;
  }

  return route.suggestWhen.some(condition => {
    // Check recipe-contains conditions
    if (condition.startsWith('recipe-contains-') && context.activeRecipe) {
      const toolId = condition.replace('recipe-contains-', '');
      return context.activeRecipe.steps?.some(step => step.toolId === toolId);
    }

    // Check on-page conditions
    if (condition.startsWith('on-') && context.currentRoute) {
      const targetPage = condition.replace('on-', '').replace('-page', '');
      return context.currentRoute.includes(targetPage);
    }

    // Check recipe-executed condition
    if (condition === 'recipe-executed' && context.lastExecutionTime) {
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      return context.lastExecutionTime > fiveMinutesAgo;
    }

    return false;
  });
};

/**
 * Get routes suggested for a specific user role
 *
 * @param {string} userRole - User's role
 * @param {Array} routes - All routes
 * @returns {Array} Routes recommended for this role
 */
export const getRoutesForRole = (userRole, routes) => {
  return routes.filter(route =>
    route.suggestFor?.includes(userRole)
  );
};

/**
 * Sort routes by priority and pinned status
 *
 * @param {Array} routes - Routes to sort
 * @param {Array} pinnedRouteIds - IDs of pinned routes
 * @returns {Array} Sorted routes
 */
export const sortRoutes = (routes, pinnedRouteIds = []) => {
  return [...routes].sort((a, b) => {
    // Pinned routes first
    const aIsPinned = pinnedRouteIds.includes(a.id);
    const bIsPinned = pinnedRouteIds.includes(b.id);

    if (aIsPinned && !bIsPinned) return -1;
    if (!aIsPinned && bIsPinned) return 1;

    // Then by priority
    return (a.priority || 999) - (b.priority || 999);
  });
};

/**
 * Check if route has available data
 * Integrates with dataStatus to check data source availability
 *
 * @param {Object} route - Route to check
 * @param {Object} dataStatus - Data status from AppContext
 * @returns {boolean} Whether route has data available
 */
export const routeHasData = (route, dataStatus) => {
  if (!route.dataSource) {
    return true; // No data requirement
  }

  if (route.dataSource === 'salesforce') {
    // Check if Salesforce is connected and data gen is complete
    return dataStatus?.salesforce_connected === true && dataStatus?.data_gen === true;
  }

  // Add more data source checks as needed
  return true;
};

/**
 * Filter routes that have available data
 *
 * @param {Array} routes - Routes to filter
 * @param {Object} dataStatus - Data status from AppContext
 * @returns {Array} Routes with available data
 */
export const getRoutesWithData = (routes, dataStatus) => {
  return routes.filter(route => routeHasData(route, dataStatus));
};

/**
 * Get recently accessed routes
 *
 * @param {Array} recentRoutes - Recent route entries with timestamps
 * @param {Array} allRoutes - All routes
 * @param {number} limit - Max number to return
 * @returns {Array} Recent route objects
 */
export const getRecentRoutes = (recentRoutes = [], allRoutes = [], limit = 5) => {
  return recentRoutes
    .slice(0, limit)
    .map(recent => allRoutes.find(r => r.id === recent.routeId))
    .filter(Boolean);
};

/**
 * Calculate route usage stats
 * (Placeholder for future analytics integration)
 *
 * @param {string} routeId - Route ID
 * @param {Object} analytics - Analytics data
 * @returns {Object} Usage statistics
 */
export const getRouteUsageStats = (routeId, analytics = {}) => {
  // Placeholder - would integrate with actual analytics
  return {
    viewCount: analytics[routeId]?.views || 0,
    lastAccessed: analytics[routeId]?.lastAccessed || null,
    avgTimeSpent: analytics[routeId]?.avgTimeSpent || 0,
    adoptionRate: analytics[routeId]?.adoptionRate || 0,
  };
};

export default {
  getVisibleRoutes,
  getSuggestedRoutes,
  getRoutesByCategory,
  shouldSuggestRoute,
  getRoutesForRole,
  sortRoutes,
  routeHasData,
  getRoutesWithData,
  getRecentRoutes,
  getRouteUsageStats,
};
